import { KeyboardEvent, useRef, useState } from "react";
import "./styles/hint.sass";
import { config, easings, useSpring, animated } from "@react-spring/web";
import { InformationCircleIcon } from "@heroicons/react/24/solid";

interface IProps {
    title: string;
    children: JSX.Element | JSX.Element[];
}
/**
 * Accordion type component.
 * It shows a main title and, on click, displays the contents
 * It can be useful in forms, to give a bit more information about what to fill in
 * @param title    The title of the hint
 * @param children  The text shown inside the hint
 */
const Hint = ({ title, children }: IProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const contents = useRef(null);
    const [props, api] = useSpring(
        () => ({
            opacity: 0,
            height: 0,
            paddingBottom: "0rem",
            marginTop: "0rem",
            config: { ...config.stiff, easing: easings.easeOutCirc },
        }),
        [],
    );
    function handleToggle() {
        setIsOpen(!isOpen);
        api.start({
            from: {
                opacity: 0,
                height: 0,
                marginTop: "0rem",
                paddingBottom: "0rem",
            },
            to: {
                opacity: 1,
                paddingBottom: "0.5rem",
                marginTop: "1rem",
                height:
                    contents.current !== null &&
                    //@ts-ignore
                    contents.current.offsetHeight + 10,
            },
            reverse: isOpen,
        });
    }
    return (
        <div
            className="hint"
            onClick={handleToggle}
            onKeyDown={(key: KeyboardEvent) => {
                if (key.key !== "Tab" && key.key !== "Space" && !key.shiftKey) {
                    handleToggle();
                }
            }}
        >
            <header className="hint__header" tabIndex={0}>
                <h4>
                    <InformationCircleIcon className="icon" /> <em>{title}</em>
                </h4>
            </header>
            <animated.div style={props} className={`hint__content`}>
                <div ref={contents}>{children}</div>
            </animated.div>
        </div>
    );
};
export default Hint;
